import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { MenuItemCard } from '../components/MenuItemCard';
import { OrderBar } from '../components/OrderBar';
import { useOrder } from '../context/OrderContext';
import { useLanguage } from '../context/LanguageContext';
import { CATEGORIES, MENU_ITEMS } from '../data/menu';
export const Menu = () => {
  const navigate = useNavigate();
  const {
    pickupDate
  } = useOrder();
  const {
    t,
    language,
    dateLocale
  } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const categoryRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const menuItems = MENU_ITEMS;
  useEffect(() => {
    if (!pickupDate) {
      navigate('/pickup');
    }
  }, [pickupDate, navigate]);
  const scrollToCategory = (id: string) => {
    setActiveCategory(id);
    const element = categoryRefs.current[id];
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140; // offset for sticky headers
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Setup intersection observer for scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveCategory(entry.target.id);
        }
      });
    }, {
      rootMargin: '-150px 0px -60% 0px'
    });
    Object.values(categoryRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);
  if (!pickupDate) return null;
  return <PageTransition className="pb-32" data-id="element-142">
      {/* Header */}
      <div className="bg-cream sticky top-0 z-40 pt-6 px-4 pb-2" data-id="element-143">
        <div className="flex items-center justify-between mb-4" data-id="element-144">
          <button onClick={() => navigate('/pickup')} className="w-10 h-10 bg-white rounded-full shadow-soft flex items-center justify-center text-espresso hover:bg-cream-dark transition-colors" data-id="element-145">
            <ChevronLeft size={20} data-id="element-146" />
          </button>
          <div className="text-right" data-id="element-147">
            <div className="text-xs text-espresso-light uppercase tracking-wider font-medium" data-id="element-148">
              {t('menu.pickup')}
            </div>
            <div className="font-serif text-terracotta-dark capitalize" data-id="element-149">
              {format(pickupDate, 'EEEE, MMM d', {
              locale: dateLocale
            })}
            </div>
          </div>
        </div>

        {/* Category Nav */}
        <div className="flex overflow-x-auto no-scrollbar gap-2 pb-2 -mx-4 px-4" data-id="element-150">
          {CATEGORIES.map(cat => <button key={cat.id} onClick={() => scrollToCategory(cat.id)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === cat.id ? 'bg-espresso text-white' : 'bg-white text-espresso shadow-soft border border-border/50'}`} data-id="element-151">
              {language === 'en' ? cat.nameEn : cat.name}
            </button>)}
        </div>
      </div>

      {/* Menu Sections */}
      <div className="px-4 mt-4 space-y-10" data-id="element-152">
        {CATEGORIES.map(category => {
        const items = menuItems.filter(item => item.categoryId === category.id);
        if (items.length === 0) return null;
        const catName = language === 'en' ? category.nameEn : category.name;
        const catDesc = language === 'en' ? category.descriptionEn : category.description;
        return <div key={category.id} id={category.id} ref={el => categoryRefs.current[category.id] = el} className="scroll-mt-36" data-id="element-153">
              <div className="mb-4" data-id="element-154">
                <h2 className="text-2xl font-serif text-espresso" data-id="element-155">{catName}</h2>
                {catDesc && <p className="text-sm text-terracotta-dark mt-1 font-medium" data-id="element-156">
                    {catDesc}
                  </p>}
              </div>
              <div className="space-y-3" data-id="element-157">
                {items.map(item => <MenuItemCard key={item.id} item={item} data-id="element-158" />)}
              </div>
            </div>;
      })}
      </div>

      <OrderBar data-id="element-159" />
    </PageTransition>;
};